// Skill definitions — source of truth for the interactive Skills planner.
// Numbers based on the official codex (Combat Stats + Skill Mastery + Player Stats pages).
//
// Each skill has:
//   max         — max rank investable (0..max)
//   cost(rank)  — skill-point cost for the rank you're currently buying (so total cost = sum of cost(1..r))
//   unlock      — minimum character level required to start investing
//   display(r)  — formatted current value at rank r
//   base(r)    — raw numeric current value
//   color       — accent (matches in-game UI)
//   icon        — lucide icon name
//   category    — "Fight Skills" | "Economic Skills"

export const FIGHT_COLOR = "#e35a5a";
export const ECON_COLOR_ENTR = "#d978ff";
export const ECON_COLOR_ENERGY = "#3aa0ff";
export const ECON_COLOR_PROD = "#f3c552";
export const HEALTH_COLOR = "#5fd99a";
export const LOOT_COLOR = "#5fd99a";

export const SKILLS = [
  {
    key: "attack",
    name: "Attack",
    category: "Fight Skills",
    icon: "Swords",
    color: FIGHT_COLOR,
    desc: "The base value for **Damage** calculation.",
    max: 10,
    unlock: 1,
    cost: (r) => r, // 1,2,3...10 → total 55 to max
    base: (r) => 100 + r * 20,
    display: (r) => `${100 + r * 20}`,
  },
  {
    key: "precision",
    name: "Precision",
    category: "Fight Skills",
    icon: "Target",
    color: FIGHT_COLOR,
    desc: "Chance to hit the target. Misses cannot crit, and deal half damage.",
    max: 10,
    unlock: 1,
    cost: (r) => r,
    base: (r) => 50 + r * 5,
    display: (r) => `${50 + r * 5}%`,
  },
  {
    key: "crit_chance",
    name: "Crit. chance",
    category: "Fight Skills",
    icon: "Zap",
    color: FIGHT_COLOR,
    desc: "Probability of landing a critical hit.",
    max: 10,
    unlock: 5,
    cost: (r) => r,
    base: (r) => 10 + r * 5,
    display: (r) => `${10 + r * 5}%`,
  },
  {
    key: "crit_damage",
    name: "Crit. damages",
    category: "Fight Skills",
    icon: "Flame",
    color: FIGHT_COLOR,
    desc: "The damage factor increase of critical hits over normal hits.",
    max: 10,
    unlock: 10,
    cost: (r) => r,
    base: (r) => 10 + r * 2,
    display: (r) => `${10 + r * 2}%`,
  },
  {
    key: "armor",
    name: "Armor",
    category: "Fight Skills",
    icon: "Shield",
    color: "#9aa3b3",
    desc: "Reduces health lost when fighting in battles.",
    max: 10,
    unlock: 5,
    cost: (r) => r,
    base: (r) => r * 4,
    display: (r) => `${r * 4}%`,
  },
  {
    key: "dodge",
    name: "Dodge",
    category: "Fight Skills",
    icon: "Wind",
    color: "#9aa3b3",
    desc: "Chance to dodge all incoming damage when fighting in battles. Dodging also prevents equipment durability from being consumed.",
    max: 10,
    unlock: 10,
    cost: (r) => r,
    base: (r) => r * 4,
    display: (r) => `${r * 4}%`,
  },
  {
    key: "health",
    name: "Health",
    category: "Fight Skills",
    icon: "Heart",
    color: HEALTH_COLOR,
    desc: "Consumed when fighting in battles.",
    max: 10,
    unlock: 5,
    cost: (r) => r,
    base: (r) => 100 + r * 20,
    display: (r) => `${100 + r * 20}`,
  },
  {
    key: "loot_chance",
    name: "Loot chance",
    category: "Fight Skills",
    icon: "Sparkles",
    color: LOOT_COLOR,
    desc: "Chance per hit to instantly loot. Each **1%** gives **1%** to common cases and **0.01%** to mythic cases per hit.",
    max: 10,
    unlock: 10,
    cost: (r) => r,
    base: (r) => 5 + r,
    display: (r) => `${5 + r}%`,
  },
  {
    key: "hunger",
    name: "Hunger",
    category: "Fight Skills",
    icon: "Utensils",
    color: "#f3c552",
    desc: "Limit of food consumption.",
    max: 10,
    unlock: 10,
    cost: (r) => r,
    base: (r) => 4 + r,
    display: (r) => `${4 + r}`,
  },

  // ===== Economic =====
  {
    key: "entrepreneurship",
    name: "Entrepreneurship",
    category: "Economic Skills",
    icon: "Briefcase",
    color: ECON_COLOR_ENTR,
    desc: "Used to **Self Work** in your own companies.",
    max: 10,
    unlock: 1,
    cost: (r) => r,
    base: (r) => 30 + r * 10,
    display: (r) => `${30 + r * 10}`,
  },
  {
    key: "energy",
    name: "Energy",
    category: "Economic Skills",
    icon: "Bolt",
    color: ECON_COLOR_ENERGY,
    desc: "Used to **Work**.",
    max: 10,
    unlock: 1,
    cost: (r) => r,
    base: (r) => 30 + r * 10,
    display: (r) => `${30 + r * 10}`,
  },
  {
    key: "production",
    name: "Production",
    category: "Economic Skills",
    icon: "Factory",
    color: ECON_COLOR_PROD,
    desc: "The **Production Points** you generate each time you work.",
    max: 10,
    unlock: 1,
    cost: (r) => r,
    base: (r) => 10 + r * 2,
    display: (r) => `${10 + r * 2}`,
  },
  {
    key: "companies_limit",
    name: "Companies limit",
    category: "Economic Skills",
    icon: "Building2",
    color: "#9aa3b3",
    desc: "Amount of companies you can own.",
    max: 10,
    unlock: 5,
    cost: (r) => r,
    base: (r) => 2 + r,
    display: (r) => `${2 + r}`,
  },
  {
    key: "management",
    name: "Management",
    category: "Economic Skills",
    icon: "Users",
    color: "#9aa3b3",
    desc: "Amount of **Workers** you can manage. And daily hire limit.",
    max: 10,
    unlock: 10,
    cost: (r) => r,
    base: (r) => 4 + r,
    display: (r) => `${4 + r}`,
  },
];

// Total skill points available at a given character level.
// Per codex: 4 SP per level. Level 1 starts with 4. Capped at 50 (200 SP).
export function pointsAt(level) {
  const lvl = Math.max(1, Math.min(50, level));
  return lvl * 4;
}

export function totalCost(ranks) {
  let total = 0;
  for (const s of SKILLS) {
    const r = ranks[s.key] || 0;
    for (let i = 1; i <= r; i++) total += s.cost(i);
  }
  return total;
}

export const STORAGE_KEY = "territoria_skill_plan_v1";
